import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { shortScreen } from '../learn_screen_parts.tsx';
import { RangeDirection } from './range_finder_session.ts';
import { arrowHeadFrom, isHopping, StairsKeyframe, stillKeyframe } from './range_stairs_choreography.ts';
import { landingTread, scrollOf, StairsBubble, StairsSceneModel, treadOf, treadsInView } from './range_stairs_model.ts';

// The stairs scene: the whole of what the direction screen says while a direction runs (issue
// #3510, addendum 3). A singer on a staircase, one tread per note he has been credited with, the
// note ahead lit on the next tread, an arrow pointing the way and a speech bubble over his head
// that carries every instruction the screen used to print as a prompt, a nudge, a hint and a
// footer message. Presentational: what there is to draw comes from the model
// (range_stairs_model.ts) and where the moving parts have got to comes from the keyframe
// (range_stairs_choreography.ts), so a story can render any moment of a run - standing still or
// mid-hop - from a state and an instant it picks by hand.
//
// Drawn as inline SVG in the accent colour, as the staircase artwork it replaces was, so it needs
// no asset and follows the theme. The bubble is HTML over the drawing rather than SVG text: it has
// to wrap, size itself to its words and stay white with dark text in both themes, all of which the
// browser does for a div and none of which it does for <text>.
//
// Geometry. Square steps of `stepSize` (tread and riser equal, so the flight runs at exactly 45
// degrees), five labelled treads in view plus a sixth flush with the landing, which closes the
// flight at the right edge. The descent is drawn as it reads - left to right and downward - and the
// ascent is that same drawing flipped top to bottom, so both directions share every number below
// and the ascent needs only its arrow nudged clear of the bottom tread. The box is fixed at
// `sceneWidth` x `sceneHeight` and contains everything, the landing's label included: that is why
// the flight starts a little higher up the box than the platform label's descenders need.
//
// The scroll is what makes the stairs endless, and it runs through fractions during a hop: the
// whole flight slides one step up and to the left while the landing stays where it is, because
// every tread's y is clamped at the landing's, so the riser joining the two stretches and the next
// tread rises out of the platform. The singer's own tread scrolls with it, which is why he hops on
// the spot while the world moves under him.
//
// Short screens. The box is a fixed size, and on an iPhone SE the screen's other fixed heights -
// the header, the segments, the meter and the foot the two buttons are reserved in - leave about
// 270 px for it. So under `shortScreen` the whole scene is scaled down as one: the box shrinks and
// its contents are scaled to match, which keeps the bubble over the singer's head (it is
// positioned in the drawing's own coordinates) and the drawing's proportions with it.
//
// The handover is the one moment two flights are drawn: the finished descent and the arriving
// ascent, offset by the same distance in opposite directions and crossing over, with the singer
// riding the landing until the ascent's first tread - which is that same landing - is under him.
//
// Motion. `prefers-reduced-motion: reduce` is the default for `reducedMotion`, which takes the arc
// out of the hop (the choreography collapses every travelling duration to nothing) and stops the
// "Hold it" pulse; a story passes it explicitly.

export const rangeStairsSceneTestId = 'range-stairs-scene';
export const rangeStairsFlightTestId = 'range-stairs-flight';
export const rangeStairsSingerTestId = 'range-stairs-singer';
export const rangeStairsBubbleTestId = 'range-stairs-bubble';
export const rangeStairsBubbleTextTestId = 'range-stairs-bubble-text';
export const rangeStairsBubbleSmallTestId = 'range-stairs-bubble-small';
export const rangeStairsTreadLabelTestId = 'range-stairs-tread-label';
export const rangeStairsTargetTestId = 'range-stairs-target';
export const rangeStairsDoneLabelTestId = 'range-stairs-done-label';
export const rangeStairsArrowTestId = 'range-stairs-arrow';

// One place to change the whole drawing's colour
const sceneColor = 'var(--color-tab-selected)';

const sceneWidth = 324;
const sceneHeight = 376;

const stepSize = 40; // tread and riser alike
const flightLeft = 26; // the left end of the topmost tread in view
// How far down the box that tread sits: high enough that the landing, five steps below it, still
// has room for its label inside the box
const flightTop = 150;
const landingY = flightTop + stepSize * treadsInView; // the platform that closes the flight
const flightRight = sceneWidth - 10; // how far the platform runs toward the edge
const flightStrokeWidth = 7;

// The singer's feet stand clear of the tread's stroke, and the bubble hangs over his head
const singerLift = flightStrokeWidth;
const singerHeadRoom = 62; // feet to the top of his head
const singerArcHeight = 34; // the top of the hop, above the line between the two treads
const bubbleGap = 14;
const bubbleEdgeGap = 4; // the bubble stays this far inside the box
const bubbleWedgeInset = 14; // and its wedge this far inside the bubble

const labelDrop = 18; // a tread's note name sits this far under the tread
const doneLabelDrop = 20;
const targetHaloRadius = 13;

// The label of a tread this close to the landing would be the platform's, so only the treads above
// it carry a name
const treadLabelMargin = 4;

// The handover slides everything left by exactly the distance that takes the singer from the middle
// of the landing's own tread to the middle of the far-left one, which is where the ascent starts him
const handoverDistance = stepSize * (treadsInView - 1);

// The arrow runs clear of the treads, from above the first tread to beyond the last. Its two ends
// differ by as much across as down, which is what keeps it parallel to a flight of square steps.
const arrowTail = { x: 150, y: flightTop - 18 };
const arrowTip = { x: 262, y: flightTop + 94 };
const arrowStrokeWidth = 3.5;
const arrowOpacity = 0.6;
const arrowBarbLength = 15;
const arrowBarbSpread = 0.5; // radians either side of the shaft
// Flipped, the ascent's arrow would start below the bottom tread, so its copy sits up and to the right
const ascentArrowNudge = { x: 20, y: -24 };

const pulseSeconds = 1.1; // the "Hold it" loop

// What the whole scene shrinks to on a short screen (an iPhone SE): 376 px of box become 271, which
// is what the screen's other fixed heights leave for it
const shortScreenScale = 0.72;

interface Point {
  x: number;
  y: number;
}

// The ascent is the descent flipped top to bottom about the flight's own middle
function flipY(direction: RangeDirection, y: number): number {
  return direction === 'lower' ? y : flightTop + landingY - y;
}

function otherDirection(direction: RangeDirection): RangeDirection {
  return direction === 'lower' ? 'higher' : 'lower';
}

// Tread `index`'s left end, in the flight's own numbering (the scroll slides the whole flight)
function treadPos(index: number, scroll: number): Point {
  const along = stepSize * (index - scroll);
  return { x: flightLeft + along, y: flightTop + along };
}

function round(value: number): string {
  return value.toFixed(2);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

// One stepped polyline - tread, riser, tread, riser - closing on the landing, which runs to the
// right edge. Every corner's y is clamped at the landing's, which is what keeps the platform still
// while the flight scrolls over it. `shift` lengthens the platform by moving the flight away from
// its far end, which is what gives the landing's label room once a direction has ended.
function flightPath(direction: RangeDirection, scroll: number, shift: number): string {
  const parts: string[] = [];
  const last = landingTread(scroll);
  for (let index = last - treadsInView; index <= last; index++) {
    const corner = treadPos(index, scroll);
    if (index === last - treadsInView) {
      parts.push(`M${round(corner.x)} ${round(flipY(direction, Math.min(corner.y, landingY)))}`);
    }
    const next = Math.min(treadPos(index + 1, scroll).y, landingY);
    parts.push(`H${round(corner.x + stepSize)}`, `V${round(flipY(direction, index < last ? next : landingY))}`);
  }
  parts.push(`H${round(flightRight + shift)}`);
  return parts.join(' ');
}

function arrowPoints(direction: RangeDirection): { tail: Point; tip: Point } {
  const nudge = direction === 'higher' ? ascentArrowNudge : { x: 0, y: 0 };
  return {
    tail: { x: arrowTail.x + nudge.x, y: flipY(direction, arrowTail.y) + nudge.y },
    tip: { x: arrowTip.x + nudge.x, y: flipY(direction, arrowTip.y) + nudge.y },
  };
}

// A small open head, its barbs taken from the shaft's own angle, so it stays square to the line
// whichever way the flight runs
function arrowHeadPath(tail: Point, tip: Point): string {
  const angle = Math.atan2(tip.y - tail.y, tip.x - tail.x);
  const barb = (offset: number): string =>
    `${round(tip.x - arrowBarbLength * Math.cos(angle + offset))} ${round(tip.y - arrowBarbLength * Math.sin(angle + offset))}`;
  return `M${barb(arrowBarbSpread)} L${round(tip.x)} ${round(tip.y)} L${barb(-arrowBarbSpread)}`;
}

// Where the singer stands: the middle of his tread, feet clear of its stroke, lifted by the hop's
// arc. His tread and the scroll are both fractional while he is in the air.
function singerPosition(direction: RangeDirection, keyframe: StairsKeyframe): Point {
  const corner = treadPos(keyframe.tread, keyframe.scroll);
  const slide = keyframe.handover ? handoverDistance * keyframe.handover.slide : 0;
  return {
    x: corner.x + stepSize / 2 - keyframe.shift * stepSize - slide,
    y: flipY(direction, Math.min(corner.y, landingY)) - singerLift - singerArcHeight * keyframe.lift,
  };
}

export const RangeStairsScene = ({
  model,
  keyframe = stillKeyframe(model),
  reducedMotion,
  bubble = model.bubble,
}: {
  model: StairsSceneModel;
  // A story pins the scene at an instant of its own; the direction screen passes the live one
  keyframe?: StairsKeyframe;
  reducedMotion?: boolean;
  // What the singer actually says: the direction screen passes the bubble its stabilizer has let
  // through (issue #3510, addendum 4), which lags the model's by up to bubbleMinShowMs. A story
  // that draws one moment of the scene needs no stabilizing, so the model's own line is the default.
  bubble?: StairsBubble;
}) => {
  const preferReduced = usePrefersReducedMotion();
  const reduced = reducedMotion ?? preferReduced;
  const { direction, notes, target, holding } = model;
  const { handover } = keyframe;
  const hopping = isHopping(keyframe.stage);
  const shift = keyframe.shift * stepSize;
  const singer = singerPosition(direction, keyframe);
  // The note ahead is not advertised while he is in the air: he has one to celebrate first
  const targetTread = target === undefined || hopping ? undefined : notes.length + 1;
  return (
    <Scene
      data-testid={rangeStairsSceneTestId}
      data-direction={direction}
      data-tread={treadOf(model)}
      data-scroll={scrollOf(model)}
      data-stage={keyframe.stage}
    >
      <Box>
        <Drawing viewBox={`0 0 ${sceneWidth} ${sceneHeight}`} aria-hidden="true">
          <Flight
            direction={direction}
            scroll={keyframe.scroll}
            shift={shift}
            slide={handover ? -handoverDistance * handover.slide : 0}
            opacity={handover ? handover.leaving : 1}
            notes={notes}
            target={target}
            targetTread={targetTread}
            onLanding={model.onLanding}
            arrowGrow={keyframe.arrowGrow}
            // No arrow during the handover: both flights are on their way somewhere
            arrowOpacity={handover ? 0 : keyframe.arrowOpacity}
          />
          {handover && (
            // The ascent arriving around him: its first tread is the landing he is standing on, so
            // it needs no labels and no arrow of its own until the direction proper begins
            <Flight
              direction={otherDirection(direction)}
              scroll={0}
              shift={0}
              slide={handoverDistance * (1 - handover.slide)}
              opacity={handover.arriving}
              notes={[]}
              onLanding={false}
              arrowGrow={0}
              arrowOpacity={0}
            />
          )}
          <Singer
            transform={`translate(${round(singer.x)} ${round(singer.y)})`}
            data-testid={rangeStairsSingerTestId}
            data-tread={treadOf(model)}
            data-holding={holding ? 'true' : 'false'}
            data-lift={keyframe.lift.toFixed(2)}
          >
            <SingerHead cx={0} cy={-50} r={11} />
            <SingerBody d="M0 -39 V-13" />
            <SingerLimb d={hopping ? 'M0 -13 L-13 -2 M0 -13 L12 -1' : 'M0 -13 L-9 0 M0 -13 L9 0'} />
            <SingerLimb d={holding ? 'M0 -33 L-12 -20 M0 -33 L13 -22' : 'M0 -33 L-10 -22 M0 -33 L11 -24'} />
            {holding && !hopping && <SoundWaves d="M17 -56 q6 6 0 12 M24 -60 q10 10 0 20" />}
          </Singer>
        </Drawing>
        <SceneBubble
          bubble={keyframe.bubble ?? bubble}
          opacity={keyframe.bubbleOpacity}
          singer={singer}
          pulse={!reduced}
        />
      </Box>
    </Scene>
  );
};

// One flight: its stairs, the note names under the treads behind him, the note lit on the tread
// ahead, the platform's label and the arrow - all of it moved together by the handover's `slide`
// and the landing's `shift`, so a whole flight can leave or arrive as one rigid thing.
const Flight = ({
  direction,
  scroll,
  shift,
  slide,
  opacity,
  notes,
  target,
  targetTread,
  onLanding,
  arrowGrow,
  arrowOpacity: arrowFade,
}: {
  direction: RangeDirection;
  scroll: number;
  shift: number;
  slide: number;
  opacity: number;
  notes: string[];
  target?: string;
  targetTread?: number;
  onLanding: boolean;
  arrowGrow: number;
  arrowOpacity: number;
}) => {
  const { tail, tip } = arrowPoints(direction);
  const shaft = Math.hypot(tip.x - tail.x, tip.y - tail.y);
  // The head arrives with the last of the shaft rather than waiting at the end for it
  const headIn = clamp01((arrowGrow - arrowHeadFrom) / (1 - arrowHeadFrom));
  const platform = treadPos(landingTread(scroll), scroll);
  const first = landingTread(scroll) - treadsInView;
  return (
    <g
      transform={`translate(${round(slide - shift)} 0)`}
      opacity={opacity}
      data-testid={rangeStairsFlightTestId}
      data-direction={direction}
    >
      <FlightPath d={flightPath(direction, scroll, shift)} />
      {Array.from({ length: treadsInView + 1 }, (_unused, offset) => {
        const index = first + offset;
        const corner = treadPos(index, scroll);
        if (corner.y > landingY - treadLabelMargin) {
          return null; // the tread flush with the landing: the platform's own label goes there
        }
        const middle = corner.x + stepSize / 2;
        const baseline = flipY(direction, corner.y) + labelDrop;
        if (index >= 1 && index <= notes.length) {
          return (
            <Label
              key={index}
              x={middle}
              y={baseline}
              data-testid={rangeStairsTreadLabelTestId}
              data-tread={index}
              data-note={notes[index - 1]}
            >
              {`${notes[index - 1]} ✓`}
            </Label>
          );
        }
        if (index !== targetTread) {
          return null;
        }
        return (
          <g key={index} data-testid={rangeStairsTargetTestId} data-tread={index} data-note={target}>
            <TargetHalo cx={middle} cy={flipY(direction, corner.y)} r={targetHaloRadius} />
            <Label x={middle} y={baseline}>{`♪ ${target}`}</Label>
          </g>
        );
      })}
      {onLanding && (
        <Label
          x={(platform.x + flightRight + shift) / 2}
          y={flipY(direction, landingY) + doneLabelDrop}
          data-testid={rangeStairsDoneLabelTestId}
        >
          {donePlatformLabel(direction)}
        </Label>
      )}
      {arrowFade > 0 && (
        <g data-testid={rangeStairsArrowTestId}>
          <Arrow
            d={`M${round(tail.x)} ${round(tail.y)} L${round(tip.x)} ${round(tip.y)}`}
            opacity={arrowOpacity * arrowFade}
            strokeDasharray={round(shaft)}
            strokeDashoffset={round(shaft * (1 - arrowGrow))}
          />
          <Arrow d={arrowHeadPath(tail, tip)} opacity={arrowOpacity * arrowFade * headIn} />
        </g>
      )}
    </g>
  );
};

// What the platform says once the singer is standing on it
export function donePlatformLabel(direction: RangeDirection): string {
  return direction === 'lower' ? 'As low as I can go' : 'As high as I can go';
}

// Whether the browser is asking for less motion. Read once for the first render and then followed,
// because a singer can turn it on with the page open.
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => matchReducedMotion()?.matches ?? false);
  useEffect(() => {
    const media = matchReducedMotion();
    if (!media) {
      return;
    }
    const update = () => setReduced(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return reduced;
}

function matchReducedMotion(): MediaQueryList | undefined {
  // jsdom has no matchMedia, and the tests that render the page there draw no frames
  return typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : undefined;
}

// The bubble is centred over the singer's head, except where that would take it outside the box:
// then it stops at the edge and the wedge alone follows him. Its width is its words', so it is
// measured after every render rather than guessed - React bails out when the width has not changed,
// and the layout effect runs before the browser paints, so the bubble is never seen off-centre.
const SceneBubble = ({
  bubble,
  opacity,
  singer,
  pulse,
}: {
  bubble: StairsSceneModel['bubble'];
  opacity: number;
  singer: Point;
  pulse: boolean;
}) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const node = bubbleRef.current;
    if (!node) {
      return;
    }
    // The measurement has to reach the state, and the first one has to happen before the browser
    // paints, or the bubble is seen off-centre for a frame
    const measure = () => setWidth(node.offsetWidth);
    measure();
    if (typeof ResizeObserver !== 'function') {
      return; // jsdom: the tests that render the page there do not look at where the bubble sits
    }
    // Every later measurement: the words change, and the font the app loads changes their width
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const left = Math.min(
    Math.max(singer.x - width / 2, bubbleEdgeGap),
    Math.max(sceneWidth - width - bubbleEdgeGap, bubbleEdgeGap),
  );
  const wedge = Math.min(
    Math.max(singer.x - left, bubbleWedgeInset),
    Math.max(width - bubbleWedgeInset, bubbleWedgeInset),
  );
  const style = {
    left: `${left}px`,
    top: `${Math.max(singer.y - singerHeadRoom - bubbleGap, bubbleEdgeGap)}px`,
    opacity,
    '--wedge-x': `${wedge}px`,
  } as CSSProperties;
  return (
    <Bubble
      ref={bubbleRef}
      style={style}
      $big={bubble.kind === 'hold'}
      data-testid={rangeStairsBubbleTestId}
      data-kind={bubble.kind}
    >
      <BubbleText $pulse={pulse && bubble.kind === 'hold'} data-testid={rangeStairsBubbleTextTestId}>
        {bubble.text}
      </BubbleText>
      {bubble.small !== undefined && <Small data-testid={rangeStairsBubbleSmallTestId}>{bubble.small}</Small>}
    </Bubble>
  );
};

const Scene = styled.div`
  flex: none;
  width: ${sceneWidth}px;
  height: ${sceneHeight}px;
  margin: 0 auto;

  ${shortScreen} {
    width: ${Math.round(sceneWidth * shortScreenScale)}px;
    height: ${Math.round(sceneHeight * shortScreenScale)}px;
  }
`;

// Everything the scene draws, at its own size: the box above is what the screen's layout sees, and
// on a short screen this is scaled inside it, so the drawing and the bubble shrink together.
const Box = styled.div`
  position: relative;
  transform-origin: top left;
  width: ${sceneWidth}px;
  height: ${sceneHeight}px;

  ${shortScreen} {
    transform: scale(${shortScreenScale});
  }
`;

const Drawing = styled.svg`
  display: block;
  width: ${sceneWidth}px;
  height: ${sceneHeight}px;
  color: ${sceneColor};
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const FlightPath = styled.path`
  stroke-width: ${flightStrokeWidth};
`;

const Label = styled.text`
  text-anchor: middle;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 12px;
  fill: currentColor;
  stroke: none;
`;

// The lit tread behind the note being played for the singer to match
const TargetHalo = styled.circle`
  fill: currentColor;
  stroke: none;
  opacity: 0.22;
`;

const Singer = styled.g`
  stroke-width: 4.5;
`;

const SingerHead = styled.circle`
  fill: currentColor;
  stroke: none;
`;

const SingerBody = styled.path`
  stroke-width: 5.5;
`;

const SingerLimb = styled.path`
  stroke-width: 4.5;
`;

// Two small arcs at mouth height, for as long as he is holding a note
const SoundWaves = styled.path`
  stroke-width: 2.2;
  opacity: 0.75;
`;

const Arrow = styled.path`
  stroke-width: ${arrowStrokeWidth};
`;

// White with dark text in both themes: it is the singer speaking, not the app's surface. The
// shadow is what keeps it a bubble in the light theme, where white on white would otherwise leave
// the words floating; a drop-shadow filter rather than a box-shadow, so the wedge below casts it too.
const Bubble = styled.div<{ $big: boolean }>`
  position: absolute;
  transform: translate(0, -100%);
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.25));
  box-sizing: border-box;
  width: max-content;
  max-width: ${sceneWidth - 24}px;
  padding: ${({ $big }) => ($big ? '10px 16px' : '9px 12px')};
  border-radius: 14px;
  background-color: #ffffff;
  color: #1e1e1e;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: ${({ $big }) => ($big ? '26px' : '16px')};
  line-height: ${({ $big }) => ($big ? '30px' : '21px')};
  text-align: center;

  // The wedge points at the top of his head wherever the bubble itself had to stop
  &::after {
    content: '';
    position: absolute;
    left: var(--wedge-x, 50%);
    bottom: -7px;
    margin-left: -7px;
    width: 14px;
    height: 14px;
    border-radius: 2px;
    background-color: #ffffff;
    transform: rotate(45deg);
  }
`;

// "Hold it" breathes, so a held note looks like something being kept up rather than a label
const pulseKeyframes = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

const BubbleText = styled.span<{ $pulse: boolean }>`
  ${({ $pulse }) =>
    $pulse &&
    css`
      display: inline-block;
      animation: ${pulseKeyframes} ${pulseSeconds}s ease-in-out infinite;
    `}
`;

const Small = styled.div`
  margin-top: 3px;
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 13px;
  line-height: 17px;
  color: #555555;
`;
